import { createContext, type ReactNode, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";

import { IgLink, IgPaper, IgTypography } from "@intelligems/ig-theme";
import {
  Box,
  Breadcrumbs,
  Stack,
  type SxProps,
  type Theme,
} from "@mui/material";

import { useRootPageTopBarSlot } from "@src/providers/layout/RootPageTopBar";
import { useAlertBannerHeight } from "@src/providers/layout/useAlertBanner";
import { IgPageTopBarActions } from "@src/utils/IgPageTopBarActions";

export interface RootPageBreadcrumb {
  label: string;
  /** Navigable ancestor. The current page omits it and renders as plain text. */
  to?: string;
}

const RootPageCrumbContext = createContext<
  ((label: string | null) => void) | null
>(null);

/**
 * Replaces the trail's last crumb — the current page — with a label only the
 * page knows, e.g. the fetched rule's name in place of the route's static
 * "Rule". Pass `null`/`undefined` while the label is still loading; the
 * static fallback shows until it resolves. Cleared automatically on unmount.
 */
export const useRootPageCurrentCrumb = (label: string | null | undefined) => {
  const setCrumb = useContext(RootPageCrumbContext);
  useEffect(() => {
    if (!setCrumb) return;
    setCrumb(label ?? null);
    return () => setCrumb(null);
  }, [setCrumb, label]);
};

/**
 * Where the page's content sits under the top bar. `"card"` — a workspace
 * that fills the viewport inside one elevated sheet. `"background"` — a list
 * page whose own sections (heading, table card) are the surfaces, so a sheet
 * behind them would nest a card in a card.
 */
export type RootPageContentSurface = "card" | "background";

export interface IgRootPageWrapperProps {
  /** Trail rendered in the top bar, first item first. */
  breadcrumbs: readonly RootPageBreadcrumb[];
  children: ReactNode;
  contentSurface?: RootPageContentSurface;
  /**
   * Styles merged onto the scrollable content region — e.g. gutters for a
   * list page. Workspace pages that manage their own frame pass nothing.
   */
  contentSx?: SxProps<Theme>;
}

/**
 * Full-viewport root page frame: a full-bleed top bar carries the breadcrumb
 * trail and the global help/settings/log-out actions, and everything below it
 * is the scroll region — the page never scrolls at document level, so the bar
 * stays pinned. `contentSurface` decides whether that region is an elevated
 * card (workspaces) or the page background itself (list pages). The AI chat
 * panel (rendered to the right by `AppLayoutShell`) shares the same
 * background. Mount it as the page root under a no-padding route provider; a
 * padded shell would double the gutters and shrink the frame off the design.
 */
export const IgRootPageWrapper = ({
  breadcrumbs,
  children,
  contentSurface = "card",
  contentSx,
}: IgRootPageWrapperProps) => {
  const navigate = useNavigate();
  const alertBannerHeight = useAlertBannerHeight();
  const [currentCrumb, setCurrentCrumb] = useImmer<string | null>(null);

  const trail = currentCrumb
    ? breadcrumbs.map((crumb, index) =>
        index === breadcrumbs.length - 1
          ? { ...crumb, label: currentCrumb }
          : crumb
      )
    : breadcrumbs;

  // When the layout shell offers its full-width top-bar slot, the bar portals
  // there so it spans the entire layout — breadcrumbs left, the global
  // actions at the far top-right, above the AI chat rail. Without a shell
  // (specs, storybook) it renders inline as the page's own first row.
  const topBarSlot = useRootPageTopBarSlot();
  const topBar = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        // Painted here (not by the page body) because the bar may portal into
        // the shell's full-width slot, outside the page's own background.
        backgroundColor: (theme) => theme.igBackgroundColors.baseLightLow,
        flexShrink: 0,

        pt: 3,
        pl: 3,
        pr: 2.5,
      }}
    >
      {/* Figma "Top bar" (15077:8945): the trail is 14px regular in the
          primary text color with "/" separators on a 10px rhythm. */}
      <Breadcrumbs
        separator="/"
        sx={{
          "& .MuiBreadcrumbs-separator": {
            color: (theme) => theme.palette.text.primary,
            mx: "5px",
          },
        }}
      >
        {trail.map((crumb) =>
          crumb.to ? (
            <IgLink
              key={crumb.label}
              component="button"
              type="button"
              variant="body1"
              onClick={() => void navigate(crumb.to!)}
              sx={{
                color: (theme) => `${theme.palette.text.primary} !important`,
                "&:hover, &:focus": {
                  cursor: "pointer",
                  color: (theme) => `${theme.palette.text.primary} !important`,
                },
              }}
            >
              {crumb.label}
            </IgLink>
          ) : (
            <IgTypography key={crumb.label} variant="body1">
              {crumb.label}
            </IgTypography>
          )
        )}
      </Breadcrumbs>
      <IgPageTopBarActions />
    </Stack>
  );

  const content = (
    <Box
      sx={[
        {
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          overflow: "auto",
        },
        ...(Array.isArray(contentSx) ? contentSx : [contentSx]),
      ]}
    >
      {children}
    </Box>
  );

  return (
    <Stack
      sx={{
        backgroundColor: (theme) => theme.igBackgroundColors.baseLightLow,
        height: `calc(100dvh - ${
          alertBannerHeight + (topBarSlot?.node ? topBarSlot.height : 0)
        }px)`,
        minHeight: 0,
      }}
    >
      {topBarSlot?.node ? createPortal(topBar, topBarSlot.node) : topBar}

      <RootPageCrumbContext.Provider value={setCurrentCrumb}>
        {contentSurface === "card" ? (
          <Stack sx={{ flex: 1, minHeight: 0, pl: 3, pr: 2.5, pt: 2, pb: 2.5 }}>
            <IgPaper
              sx={{
                boxShadow: (theme) => theme.igShadows.low,
                display: "flex",
                flex: 1,
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden",
                p: 0,
              }}
            >
              {content}
            </IgPaper>
          </Stack>
        ) : (
          content
        )}
      </RootPageCrumbContext.Provider>
    </Stack>
  );
};
